import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Package, Truck, Calendar, WarningOctagon, Buildings, Trophy, Sparkle } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/dateUtils";
import { SortableTh, useSortableRows } from "@/components/SortableTh";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line,
} from "recharts";

const fmtTL = (n) => `₺${(Number(n) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Kpi = ({ label, value, icon: Icon, accent = "#1B4776", testId }) => (
  <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white h-full" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{label}</div>
        <div className="text-3xl font-black tracking-tight text-slate-900 mt-2 font-heading">{value}</div>
      </div>
      <div className="w-11 h-11 flex items-center justify-center" style={{ background: accent }}>
        <Icon size={22} weight="duotone" className="text-white" />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { activeSiteId, siteParams } = useSites();
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [bySupplier, setBySupplier] = useState([]);
  const [recent, setRecent] = useState([]);
  const [widgets, setWidgets] = useState(null); // { totals, top_suppliers, recent_materials, range }

  const { sort, toggleSort, sortedRows: sortedRecent } = useSortableRows(recent, {
    defaultKey: "delivery_date", defaultDir: "desc",
    getters: {
      delivery_date: (r) => r.delivery_date || "",
      supplier_name: (r) => r.supplier_name || "",
      material_name: (r) => r.material_name || "",
      quantity: (r) => Number(r.quantity) || 0,
      site_location: (r) => r.site_location || "",
      created_by_name: (r) => r.created_by_name || "",
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const params = siteParams();
        const [s, t, sup, r, w] = await Promise.all([
          axios.get(`${API}/reports/summary`, { params }),
          axios.get(`${API}/reports/timeline`, { params }),
          axios.get(`${API}/reports/by-supplier`, { params }),
          axios.get(`${API}/materials`, { params }),
          axios.get(`${API}/reports/dashboard-widgets`, { params: { ...params, days: 7 } }),
        ]);
        setSummary(s.data);
        setTimeline(t.data);
        setBySupplier(sup.data.slice(0, 6));
        setRecent(r.data.slice(0, 8));
        setWidgets(w.data);
      } catch (e) { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, [activeSiteId]);

  return (
    <Shell title="Kontrol Paneli" subtitle="Genel Bakış">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Toplam Giriş" value={summary?.total_entries ?? "-"} icon={Package} testId="kpi-total" />
        <Kpi label="Aktif Şantiye" value={summary?.site_count ?? "-"} icon={Buildings} accent="#8595B4" testId="kpi-sites" />
        <Kpi label="Bu Ay Giriş" value={summary?.monthly_entries ?? "-"} icon={Calendar} accent="#059669" testId="kpi-month" />
        <Kpi label="Kritik Stok" value={summary?.low_stock_count ?? "-"} icon={WarningOctagon} accent="#DC2626" testId="kpi-low" />
      </div>

      {/* Bu Hafta widgets */}
      {widgets && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {/* Bu hafta gelen malzemeler */}
          <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm" data-testid="widget-this-week">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#95C11F]">Son 7 Gün</div>
                <div className="text-lg font-bold text-slate-900 font-heading">Bu Hafta Gelen Malzemeler</div>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-[#95C11F]">
                <Truck size={20} weight="duotone" className="text-white" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Giriş Sayısı</div>
                <div className="text-2xl font-black text-slate-900 font-heading" data-testid="widget-week-count">
                  {widgets.totals.count}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Toplam Miktar</div>
                <div className="text-2xl font-black text-slate-900 font-heading">
                  {widgets.totals.qty.toLocaleString("tr-TR")}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Toplam Değer</div>
                <div className="text-xl font-black text-[#1B4776] font-heading">
                  {widgets.totals.value > 0 ? fmtTL(widgets.totals.value) : "-"}
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Son Girişler</div>
              {widgets.recent_materials.length === 0 ? (
                <div className="text-xs text-slate-400 italic">Bu hafta kayıt yok.</div>
              ) : (
                <ul className="space-y-1.5">
                  {widgets.recent_materials.map((m, i) => (
                    <li key={i} className="flex items-center justify-between text-xs border-b border-slate-50 pb-1" data-testid={`widget-recent-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 truncate">{m.material_name}</div>
                        <div className="text-[10px] text-slate-500">{m.supplier_name} · {m.site_name}</div>
                      </div>
                      <div className="text-right ml-2 shrink-0">
                        <div className="font-mono font-bold text-slate-700">{m.quantity} {m.unit}</div>
                        <div className="text-[10px] text-slate-400">{fmtDate(m.delivery_date)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          {/* Top 3 firma */}
          <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm" data-testid="widget-top-suppliers">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#95C11F]">Son 7 Gün</div>
                <div className="text-lg font-bold text-slate-900 font-heading">En Aktif 3 Firma</div>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-[#1B4776]">
                <Trophy size={20} weight="duotone" className="text-white" />
              </div>
            </div>
            {widgets.top_suppliers.length === 0 ? (
              <div className="text-xs text-slate-400 italic mt-6">Bu hafta veri yok.</div>
            ) : (
              <ol className="space-y-3">
                {widgets.top_suppliers.map((s, i) => {
                  const medal = ["#FBBF24", "#94A3B8", "#B45309"][i] || "#CBD5E1";
                  return (
                    <li key={s.supplier} className="flex items-center gap-3 p-3 border border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`widget-supplier-${i}`}>
                      <div className="w-9 h-9 flex items-center justify-center font-black text-white text-lg font-heading" style={{ background: medal }}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-900 truncate">{s.supplier}</div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-3">
                          <span><b className="text-slate-700">{s.count}</b> giriş</span>
                          <span><b className="text-slate-700">{s.qty.toLocaleString("tr-TR")}</b> miktar</span>
                          {s.total > 0 && <span><b className="text-slate-700">{fmtTL(s.total)}</b></span>}
                        </div>
                      </div>
                      {i === 0 && <Sparkle size={14} weight="fill" className="text-[#FBBF24] shrink-0" />}
                    </li>
                  );
                })}
              </ol>
            )}
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="lg:col-span-2 p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Zaman Çizelgesi</div>
          <div className="text-lg font-bold text-slate-900 mb-4 font-heading">Malzeme Girişleri</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#1B4776" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Tedarikçi Payı</div>
          <div className="text-lg font-bold text-slate-900 mb-4 font-heading">Firma Bazında</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={bySupplier}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="supplier" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#8595B4" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="mt-4 p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm" data-testid="recent-table">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Son Girişler</div>
        <div className="text-lg font-bold text-slate-900 mb-4 font-heading">Sahadan Gelen Kayıtlar</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200">
                <SortableTh label="Tarih" k="delivery_date" sort={sort} onSort={toggleSort} className="text-left py-3" />
                <SortableTh label="Firma" k="supplier_name" sort={sort} onSort={toggleSort} className="text-left py-3" />
                <SortableTh label="Malzeme" k="material_name" sort={sort} onSort={toggleSort} className="text-left py-3" />
                <SortableTh label="Miktar" k="quantity" sort={sort} onSort={toggleSort} className="text-right py-3" align="right" />
                <SortableTh label="Şantiye" k="site_location" sort={sort} onSort={toggleSort} className="text-left py-3" />
                <SortableTh label="Personel" k="created_by_name" sort={sort} onSort={toggleSort} className="text-left py-3" />
              </tr>
            </thead>
            <tbody>
              {sortedRecent.map((r, i) => (
                <tr key={r.id} className={i % 2 ? "bg-slate-50" : "bg-white"} data-testid={`recent-row-${i}`}>
                  <td className="py-3">{fmtDate(r.delivery_date)}</td>
                  <td className="py-3 font-medium">{r.supplier_name}</td>
                  <td className="py-3">{r.material_name}</td>
                  <td className="py-3 text-right font-mono">{r.quantity} {r.unit}</td>
                  <td className="py-3 text-slate-600">{r.site_location || "-"}</td>
                  <td className="py-3 text-slate-600">{r.created_by_name}</td>
                </tr>
              ))}
              {sortedRecent.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-slate-500">Henüz kayıt yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </Shell>
  );
}
